"use client";

import Image from "next/image";

import { motion, type Variants } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

import { CTAButton } from "@/components/cta-button";
import { WHATSAPP_LINK } from "@/lib/constants";

const heroVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const heroTransition = (delay = 0) => ({
  duration: 0.3,
  ease: "easeOut" as const,
  delay,
});

export function Hero() {
  return (
    <header className="relative isolate overflow-hidden bg-linear-to-b from-[#071952] via-[#082e5a] to-[#04122b] text-white -my-18">
      <div className="mx-auto flex min-h-[75vh] w-full max-w-6xl flex-col gap-12 px-6 pb-20 pt-40 md:pt-48 lg:px-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={heroVariants}
          transition={heroTransition(0)}
          className="
    flex items-center gap-4
    -mt-6 sm:-mt-8
  "
        >
          {/* accent line */}
          <span className="h-px w-10 bg-linear-to-r from-secondary to-primary" />

          {/* label */}
          <span className="text-xs font-semibold tracking-[0.35em] text-secondary">
            Kami Buatkan Website untuk Pertumbuhan Bisnis
          </span>
        </motion.div>


        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-center">
          <div className="text-center lg:text-left">
            <motion.h1
              className="text-balance text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-6xl"
              initial="hidden"
              animate="visible"
              variants={heroVariants}
              transition={heroTransition(0.1)}
            >
              Bikin Website yang Bantu Datangkan Pelanggan dan Bikin Bisnis Makin Dikenal
            </motion.h1>

            <motion.p
              className="mx-auto mt-6 max-w-2xl text-sm text-white/70 sm:text-lg lg:mx-0"
              initial="hidden"
              animate="visible"
              variants={heroVariants}
              transition={heroTransition(0.2)}
            >
              Kamu akan punya website menarik dan desain keren dengan domain resmi, di optimasi SEO, dan tampilan mobile-friendly yang siap mendatangkan lebih banyak pelanggan!
            </motion.p>
            <motion.p
              className="mx-auto mt-4 max-w-2xl text-lg text-white lg:mx-0"
              initial="hidden"
              animate="visible"
              variants={heroVariants}
              transition={heroTransition(0.25)}
            >
              Mulai{" "}
              <span className="text-3xl font-bold text-white">Rp. 350.000 </span>{" "}saja!
            </motion.p>

            <motion.div
              className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start"
              initial="hidden"
              animate="visible"
              variants={heroVariants}
              transition={heroTransition(0.3)}
            >
              <CTAButton
                href={WHATSAPP_LINK}
                text="Konsultasi Gratis"
                size="lg"
                className="text-base shadow-[0_25px_60px_-30px_rgba(53,162,159,.8)]"
              />
              <CTAButton
                href="#pricing"
                text="Lihat Paket & Portofolio"
                variant="outline"
                size="lg"
                className="text-base text-white"
                Icon={ArrowUpRight}
              />
            </motion.div>
          </div>

          <motion.div
            className="relative mx-auto flex w-full max-w-md flex-col gap-6 lg:mx-0"
            initial="hidden"
            animate="visible"
            variants={heroVariants}
            transition={heroTransition(0.35)}
          >
            <div className="relative isolate flex h-120 items-center justify-center">
              <div className="absolute inset-0">
                <div className="absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-[#35A29F]/30 blur-[110px]" />
                <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-[#088395]/30 blur-[140px]" />
              </div>
              <div className="relative z-10 flex w-full max-w-sm items-center justify-center">
                <div className="absolute -left-8 top-10 hidden h-32 w-24 -rotate-6 rounded-3xl border border-white/20 bg-white/10 shadow-[0_30px_100px_-50px_rgba(6,182,212,1)] backdrop-blur-xl sm:block" />
                <div className="relative w-full max-w-sm translate-x-2 rotate-[-4deg] rounded-[36px] border border-white/30 bg-linear-to-b from-[#F2F7A1]/70 to-white/40 p-4 shadow-[0_50px_140px_-60px_rgba(8,131,149,1)] backdrop-blur-3xl">
                  <div className="rounded-[28px] border border-white/40 bg-black/80 p-4">
                    <div className="flex items-center justify-between text-xs text-white/70">
                      <span>WallSite Studio</span>
                      <span>Live Preview</span>
                    </div>
                    <div className="mt-3 overflow-hidden rounded-2xl border border-white/10">
                      <Image
                        src="https://res.cloudinary.com/dqywu178b/image/upload/f_auto,q_auto,w_1200/v1766063858/31262584_7799149_nerdap.jpg"
                        alt="Mockup website modern"
                        width={640}
                        height={420}
                        className="h-64 w-full object-cover"
                        priority
                        sizes="(min-width: 1024px) 420px, 90vw"
                      />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2 text-xs text-white/70">
                      {["landing page", "website perusahaan", "desktop", "android", "sistem informasi"].map((chip) => (
                        <span
                          key={chip}
                          className="rounded-full border border-white/20 px-3 py-1"
                        >
                          {chip}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="absolute -right-8 -bottom-6 rounded-3xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white shadow-[0_25px_80px_-60px_rgba(8,131,149,1)]">
                    <p className="font-semibold">Tampilan disesuaikan dengan brand Anda.</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-10 h-24 w-64 rounded-full bg-black/30 blur-3xl" />
            </div>
            <div className="flex items-center justify-between rounded-3xl border border-white/60 bg-white/70 px-5 py-4 text-sm text-zinc-700 shadow-[0_35px_100px_-70px_rgba(14,165,233,1)] backdrop-blur-xl">
              <div>
                <p className="font-semibold text-zinc-900">
                  70+ brand lokal & perusahaan memulai dari konsultasi gratis
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.dl
          className="grid w-full gap-6 rounded-3xl border border-white/20 bg-white/10 p-6 text-left text-white shadow-[0_40px_110px_-60px_rgba(7,25,82,1)] backdrop-blur-lg sm:grid-cols-3"
          initial="hidden"
          animate="visible"
          variants={heroVariants}
          transition={heroTransition(0.4)}
        >
            {[
              { label: "Klien UMKM & perusahaan", value: "70+" },
              { label: "Pengalaman proyek", value: "4 tahun" },
              { label: "Niche yang sudah kami garap", value: "12+" },
            ].map((item) => (
            <div key={item.label}>
              <dt className="text-sm uppercase tracking-wide text-white/70">
                {item.label}
              </dt>
              <dd className="text-2xl font-semibold text-white">
                {item.value}
              </dd>
            </div>
          ))}
        </motion.dl>
      </div>
      <div className="pointer-events-none absolute inset-x-1/2 top-16 -z-10 h-72 w-[80vw] -translate-x-1/2 rounded-full bg-[#088395]/40 blur-3xl" />
    </header>
  );
}
